/**
 * Locale-aware Pro subscription list prices for marketing/display.
 * Amounts are explicit regional list prices (not FX conversion).
 * Web Stripe checkout may still charge USD until multi-currency Price IDs exist;
 * iOS/Android use App Store / Play localized prices.
 */

/** @typedef {'USD' | 'EUR' | 'CHF' | 'GBP'} SubscriptionDisplayCurrency */

export const SUBSCRIPTION_DISPLAY_CURRENCIES = Object.freeze(['USD', 'EUR', 'CHF', 'GBP']);

/**
 * Regional list prices aligned to common store tiers for Pro.
 * monthly / yearly in major currency units; monthlyEffectiveYearly = yearly/12 rounded.
 */
export const SUBSCRIPTION_LIST_PRICES = Object.freeze({
  USD: Object.freeze({ monthly: 9.99, yearly: 79.99, currency: 'USD' }),
  EUR: Object.freeze({ monthly: 9.99, yearly: 79.99, currency: 'EUR' }),
  CHF: Object.freeze({ monthly: 9.9, yearly: 79.0, currency: 'CHF' }),
  GBP: Object.freeze({ monthly: 8.99, yearly: 69.99, currency: 'GBP' }),
});

/**
 * @param {string} [locale]
 * @returns {SubscriptionDisplayCurrency}
 */
export function resolveSubscriptionCurrencyFromLocale(locale) {
  const normalized = String(locale || '').toUpperCase().replace(/_/g, '-');
  if (normalized.includes('-CH') || normalized.endsWith('-CH') || normalized === 'CH') {
    return 'CHF';
  }
  if (normalized.includes('-GB') || normalized.startsWith('EN-GB')) {
    return 'GBP';
  }
  if (
    normalized.includes('-DE') ||
    normalized.includes('-FR') ||
    normalized.includes('-ES') ||
    normalized.includes('-IT') ||
    normalized.includes('-NL') ||
    normalized.includes('-AT') ||
    normalized.includes('-BE') ||
    normalized.includes('-PT') ||
    normalized.includes('-IE') ||
    normalized.includes('-FI') ||
    normalized.includes('-LU') ||
    normalized.startsWith('DE') ||
    normalized.startsWith('FR') ||
    normalized.startsWith('IT') ||
    normalized.startsWith('ES') ||
    normalized.startsWith('NL') ||
    normalized.startsWith('PT')
  ) {
    return 'EUR';
  }
  return 'USD';
}

/**
 * @returns {{ locale: string, currency: SubscriptionDisplayCurrency }}
 */
export function resolveBrowserSubscriptionLocale() {
  try {
    if (typeof navigator !== 'undefined' && navigator.language) {
      const locale = navigator.language;
      return { locale, currency: resolveSubscriptionCurrencyFromLocale(locale) };
    }
  } catch {
    // ignore
  }
  return { locale: 'en-US', currency: 'USD' };
}

/**
 * @param {number} amount
 * @param {SubscriptionDisplayCurrency} currency
 * @param {string} [locale]
 */
export function formatSubscriptionMoney(amount, currency, locale = 'en-US') {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    const fallback = SUBSCRIPTION_LIST_PRICES[currency] ? currency : 'USD';
    return `${fallback} ${Number(amount).toFixed(2)}`;
  }
}

/**
 * @param {SubscriptionDisplayCurrency} [currency]
 * @param {string} [locale]
 */
export function getSubscriptionDisplayPricing(currency = 'USD', locale = 'en-US') {
  const list = SUBSCRIPTION_LIST_PRICES[currency] ?? SUBSCRIPTION_LIST_PRICES.USD;
  const monthlyEffectiveYearly = Math.round((list.yearly / 12) * 100) / 100;
  const savingsPercent = Math.round((1 - list.yearly / (list.monthly * 12)) * 100);
  return {
    currency: list.currency,
    locale,
    monthly: list.monthly,
    yearly: list.yearly,
    monthlyEffectiveYearly,
    savingsPercent,
    freeFormatted: formatSubscriptionMoney(0, list.currency, locale),
    monthlyFormatted: formatSubscriptionMoney(list.monthly, list.currency, locale),
    yearlyFormatted: formatSubscriptionMoney(list.yearly, list.currency, locale),
    monthlyEffectiveYearlyFormatted: formatSubscriptionMoney(
      monthlyEffectiveYearly,
      list.currency,
      locale
    ),
    isIndicative: list.currency !== 'USD',
    checkoutNote:
      list.currency === 'USD'
        ? 'Web checkout is billed in USD. On iOS and Android, App Store and Google Play show your local store price.'
        : `Prices shown in ${list.currency} for your region. Web checkout currently processes in USD until regional Stripe prices are enabled. iOS and Android use your store's local price.`,
  };
}
